import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Subscription Plans
export const AI_TUTOR_PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    priceMonthly: 0,
    priceYearly: 0,
    doubtsPerDay: 1,
    imagesPerDay: 0,
    features: [
      '1 AI doubt per day',
      'No image uploads',
      'Basic answers',
      'All subjects support',
      'Community support',
    ],
    color: 'gray',
  },
  BASIC: {
    id: 'basic',
    name: 'Basic',
    priceMonthly: 83.90, // Base price (₹99 with GST)
    priceYearly: 847, // Base price (₹999 with GST)
    doubtsPerDay: 10,
    imagesPerDay: 1,
    features: [
      '10 AI doubts per day',
      '1 image upload per day',
      'All subjects support',
      'Concise answers',
      'Basic explanations',
    ],
    color: 'blue',
  },
  PRO: {
    id: 'pro',
    name: 'Pro',
    priceMonthly: 168.64, // Base price (₹199 with GST)
    priceYearly: 1694, // Base price (₹1999 with GST)
    doubtsPerDay: 30,
    imagesPerDay: 5,
    features: [
      '30 AI doubts per day',
      '5 image uploads per day',
      'All subjects support',
      'Detailed explanations',
      'Step-by-step solutions',
      'Priority responses',
    ],
    color: 'purple',
    popular: true,
  },
  ADVANCED: {
    id: 'advanced',
    name: 'Advanced',
    priceMonthly: 338.14, // Base price (₹399 with GST)
    priceYearly: 3389, // Base price (₹3999 with GST)
    doubtsPerDay: 100,
    imagesPerDay: 10,
    features: [
      '100 AI doubts per day',
      '10 image uploads per day',
      'All subjects support',
      'Unlimited explanations',
      'Step-by-step solutions',
      'Priority responses',
      'Diagram descriptions',
      'Study guidance',
    ],
    color: 'indigo',
  },
};

class AITutorSubscriptionService {
  constructor() {
    this.CACHE_KEY = 'mahstry_ai_tutor_usage';
    this.CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  }

  // Get cached usage data
  getCachedUsage(userId) {
    try {
      const cacheKey = `${this.CACHE_KEY}_${userId}`;
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      const today = new Date().toISOString().split('T')[0];
      
      // Check if cache is from today
      if (data.date !== today) {
        // Clear old cache
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return data;
    } catch (error) {
      console.error('Error reading cache:', error);
      return null;
    }
  }

  // Set cached usage data
  setCachedUsage(userId, data) {
    try {
      const cacheKey = `${this.CACHE_KEY}_${userId}`;
      const today = new Date().toISOString().split('T')[0];
      
      const cacheData = {
        ...data,
        date: today,
        timestamp: Date.now(),
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error writing cache:', error);
    }
  }

  // Clear cached usage data
  clearCachedUsage(userId) {
    try {
      const cacheKey = `${this.CACHE_KEY}_${userId}`;
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  // Get user's subscription
  async getUserSubscription(userId) {
    try {
      const docRef = doc(db, 'aiTutorSubscriptions', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          success: true,
          subscription: data,
        };
      } else {
        // Return free plan by default
        return {
          success: true,
          subscription: {
            userId,
            plan: 'free',
            doubtsUsedToday: 0,
            imagesUsedToday: 0,
            lastResetDate: new Date().toISOString().split('T')[0],
            createdAt: new Date().toISOString(),
          },
        };
      }
    } catch (error) {
      console.error('Error getting subscription:', error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  // Check if user can use AI Tutor (with caching)
  async canUseAITutor(userId) {
    try {
      // Try to get from cache first
      const cached = this.getCachedUsage(userId);
      
      if (cached && cached.plan) {
        const plan = AI_TUTOR_PLANS[cached.plan.toUpperCase()] || AI_TUTOR_PLANS.FREE;
        const remaining = plan.doubtsPerDay - (cached.doubtsUsedToday || 0);
        
        return {
          canUse: remaining > 0,
          reason: remaining <= 0 ? 'limit_reached' : null,
          remaining: Math.max(0, remaining),
          total: plan.doubtsPerDay,
          plan: plan,
        };
      }

      // If not in cache, fetch from Firebase
      const result = await this.getUserSubscription(userId);
      if (!result.success) {
        return { canUse: false, reason: 'Error fetching subscription' };
      }

      const subscription = result.subscription;
      const plan = AI_TUTOR_PLANS[subscription.plan.toUpperCase()] || AI_TUTOR_PLANS.FREE;

      // Reset daily count if it's a new day
      const today = new Date().toISOString().split('T')[0];
      let doubtsUsedToday = subscription.doubtsUsedToday || 0;
      let imagesUsedToday = subscription.imagesUsedToday || 0;
      
      if (subscription.lastResetDate !== today) {
        doubtsUsedToday = 0;
        imagesUsedToday = 0;
        // Update Firebase with reset
        await this.resetDailyUsage(userId);
      }

      // Cache the data
      this.setCachedUsage(userId, {
        plan: subscription.plan,
        doubtsUsedToday,
        imagesUsedToday,
      });

      // Check if user has remaining doubts
      const remaining = plan.doubtsPerDay - doubtsUsedToday;
      
      return {
        canUse: remaining > 0,
        reason: remaining <= 0 ? 'limit_reached' : null,
        remaining: Math.max(0, remaining),
        total: plan.doubtsPerDay,
        plan: plan,
      };
    } catch (error) {
      console.error('Error checking AI Tutor access:', error);
      return { canUse: false, reason: 'error' };
    }
  }

  // Increment usage count (update cache and Firebase)
  async incrementUsage(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Update cache first for instant UI update
      const cached = this.getCachedUsage(userId);
      if (cached) {
        cached.doubtsUsedToday = (cached.doubtsUsedToday || 0) + 1;
        this.setCachedUsage(userId, cached);
      }

      // Then update Firebase
      const docRef = doc(db, 'aiTutorSubscriptions', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Reset if new day
        if (data.lastResetDate !== today) {
          await updateDoc(docRef, {
            doubtsUsedToday: 1,
            imagesUsedToday: 0,
            lastResetDate: today,
            lastUsedAt: serverTimestamp(),
          });
        } else {
          await updateDoc(docRef, {
            doubtsUsedToday: increment(1),
            lastUsedAt: serverTimestamp(),
          });
        }
      } else {
        // Create new subscription record
        await setDoc(docRef, {
          userId,
          plan: 'free',
          doubtsUsedToday: 1,
          imagesUsedToday: 0,
          lastResetDate: today,
          createdAt: serverTimestamp(),
          lastUsedAt: serverTimestamp(),
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error incrementing usage:', error);
      return { success: false, error: error.message };
    }
  }

  // Reset daily usage
  async resetDailyUsage(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const docRef = doc(db, 'aiTutorSubscriptions', userId);

      await updateDoc(docRef, {
        doubtsUsedToday: 0,
        imagesUsedToday: 0,
        lastResetDate: today,
      });

      return { success: true };
    } catch (error) {
      console.error('Error resetting usage:', error);
      return { success: false, error: error.message };
    }
  }

  // Upgrade subscription
  async upgradeSubscription(userId, planId, paymentDetails = {}) {
    try {
      const today = new Date().toISOString().split('T')[0];
      const docRef = doc(db, 'aiTutorSubscriptions', userId);
      const docSnap = await getDoc(docRef);

      const subscriptionData = {
        userId,
        plan: planId,
        doubtsUsedToday: 0,
        imagesUsedToday: 0,
        lastResetDate: today,
        updatedAt: serverTimestamp(),
        paymentDetails: {
          ...paymentDetails,
          paidAt: serverTimestamp(),
        },
      };

      if (docSnap.exists()) {
        await updateDoc(docRef, subscriptionData);
      } else {
        await setDoc(docRef, {
          ...subscriptionData,
          createdAt: serverTimestamp(),
        });
      }

      // Clear cache to force refresh
      this.clearCachedUsage(userId);

      return { success: true };
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      return { success: false, error: error.message };
    }
  }

  // Check if user can upload images (with caching)
  async canUploadImage(userId) {
    try {
      // Try to get from cache first
      const cached = this.getCachedUsage(userId);
      
      if (cached && cached.plan) {
        const plan = AI_TUTOR_PLANS[cached.plan.toUpperCase()] || AI_TUTOR_PLANS.FREE;
        
        // Check if plan allows image uploads
        if (plan.imagesPerDay === 0) {
          return {
            canUpload: false,
            reason: 'plan_limit',
            remaining: 0,
            total: 0,
            plan: plan,
          };
        }
        
        const remaining = plan.imagesPerDay - (cached.imagesUsedToday || 0);
        
        return {
          canUpload: remaining > 0,
          reason: remaining <= 0 ? 'limit_reached' : null,
          remaining: Math.max(0, remaining),
          total: plan.imagesPerDay,
          plan: plan,
        };
      }

      // If not in cache, fetch from Firebase
      const result = await this.getUserSubscription(userId);
      if (!result.success) {
        return { canUpload: false, reason: 'Error fetching subscription' };
      }

      const subscription = result.subscription;
      const plan = AI_TUTOR_PLANS[subscription.plan.toUpperCase()] || AI_TUTOR_PLANS.FREE;

      // Check if plan allows image uploads
      if (plan.imagesPerDay === 0) {
        return {
          canUpload: false,
          reason: 'plan_limit',
          remaining: 0,
          total: 0,
          plan: plan,
        };
      }

      // Reset daily count if it's a new day
      const today = new Date().toISOString().split('T')[0];
      let imagesUsedToday = subscription.imagesUsedToday || 0;
      
      if (subscription.lastResetDate !== today) {
        imagesUsedToday = 0;
        await this.resetDailyUsage(userId);
      }

      // Cache the data
      this.setCachedUsage(userId, {
        plan: subscription.plan,
        doubtsUsedToday: subscription.doubtsUsedToday || 0,
        imagesUsedToday,
      });

      // Check if user has remaining image uploads
      const remaining = plan.imagesPerDay - imagesUsedToday;

      return {
        canUpload: remaining > 0,
        reason: remaining <= 0 ? 'limit_reached' : null,
        remaining: Math.max(0, remaining),
        total: plan.imagesPerDay,
        plan: plan,
      };
    } catch (error) {
      console.error('Error checking image upload access:', error);
      return { canUpload: false, reason: 'error' };
    }
  }

  // Increment image usage count (update cache and Firebase)
  async incrementImageUsage(userId) {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Update cache first for instant UI update
      const cached = this.getCachedUsage(userId);
      if (cached) {
        cached.imagesUsedToday = (cached.imagesUsedToday || 0) + 1;
        this.setCachedUsage(userId, cached);
      }

      // Then update Firebase
      const docRef = doc(db, 'aiTutorSubscriptions', userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        
        // Reset if new day
        if (data.lastResetDate !== today) {
          await updateDoc(docRef, {
            doubtsUsedToday: 0,
            imagesUsedToday: 1,
            lastResetDate: today,
            lastUsedAt: serverTimestamp(),
          });
        } else {
          await updateDoc(docRef, {
            imagesUsedToday: increment(1),
            lastUsedAt: serverTimestamp(),
          });
        }
      } else {
        // Create new subscription record
        await setDoc(docRef, {
          userId,
          plan: 'free',
          doubtsUsedToday: 0,
          imagesUsedToday: 1,
          lastResetDate: today,
          createdAt: serverTimestamp(),
          lastUsedAt: serverTimestamp(),
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error incrementing image usage:', error);
      return { success: false, error: error.message };
    }
  }

  // Get plan details
  getPlanDetails(planId) {
    return AI_TUTOR_PLANS[planId.toUpperCase()] || AI_TUTOR_PLANS.FREE;
  }

  // Get all plans
  getAllPlans() {
    return Object.values(AI_TUTOR_PLANS);
  }
}

export const aiTutorSubscription = new AITutorSubscriptionService();
